import { File, FileText, Film, Image as ImageIcon, LoaderCircle, Paperclip, Plus, Trash2, X } from "lucide-react";
import { createPortal } from "react-dom";
import { attachmentRawUrl } from "../api";
import type { AttachmentRecord } from "../types";

interface AttachmentManagerProps {
  attachments: AttachmentRecord[];
  uploading: { name: string; progress: number } | null;
  error: string;
  onAdd(): void;
  onRemove(attachment: AttachmentRecord): void;
  onClose(): void;
}

export function AttachmentManager(props: AttachmentManagerProps) {
  return createPortal((
    <div className="attachment-manager-layer">
      <button className="attachment-manager-backdrop" type="button" onClick={props.onClose} aria-label="Fermer" />
      <section className="attachment-manager" role="dialog" aria-modal="true" aria-label="Pièces jointes">
        <header>
          <span><Paperclip aria-hidden="true" /></span>
          <div>
            <strong>Pièces jointes</strong>
            <small>{managerSubtitle(props.attachments.length, props.uploading)}</small>
          </div>
          <button className="icon-command" type="button" onClick={props.onClose} aria-label="Fermer" title="Fermer">
            <X aria-hidden="true" />
          </button>
        </header>

        <div className="attachment-manager-list">
          {props.attachments.map((attachment) => (
            <div className="attachment-manager-row" key={attachment.id}>
              <span className="attachment-manager-preview">
                {canPreviewImage(attachment) ? (
                  <img src={attachmentRawUrl(attachment.id)} alt="" />
                ) : attachment.kind === "video" ? <Film aria-hidden="true" /> : attachment.kind === "image" ? <ImageIcon aria-hidden="true" /> : attachment.kind === "pdf" ? <FileText aria-hidden="true" /> : <File aria-hidden="true" />}
              </span>
              <span className="attachment-manager-copy">
                <strong>{attachment.originalName}</strong>
                <small>{attachment.kind === "video" ? "Vidéo · " : ""}{formatBytes(attachment.sizeBytes)}</small>
              </span>
              <button type="button" onClick={() => props.onRemove(attachment)} aria-label={`Retirer ${attachment.originalName}`} title="Retirer">
                <Trash2 aria-hidden="true" />
              </button>
            </div>
          ))}
          {props.uploading ? (
            <div className="attachment-manager-row uploading">
              <span className="attachment-manager-preview"><LoaderCircle aria-hidden="true" /></span>
              <span className="attachment-manager-copy">
                <strong>{props.uploading.name}</strong>
                <small>Upload {props.uploading.progress}%</small>
              </span>
            </div>
          ) : null}
          {!props.attachments.length && !props.uploading ? (
            <div className="attachment-manager-empty">
              <Paperclip aria-hidden="true" />
              <strong>Aucune pièce jointe</strong>
            </div>
          ) : null}
          <p className="attachment-manager-limits">5 fichiers · 200 Mo au total<br />Vidéos : 100 Mo · Images et documents : 25 Mo par fichier</p>
          {props.error ? <p className="attachment-manager-error" role="alert">{props.error}</p> : null}
        </div>

        <footer>
          <button className="text-command" type="button" onClick={props.onAdd} disabled={Boolean(props.uploading) || props.attachments.length >= 5}>
            <Plus aria-hidden="true" />
            Ajouter
          </button>
          <button className="text-command primary" type="button" onClick={props.onClose}>
            Terminer
          </button>
        </footer>
      </section>
    </div>
  ), document.body);
}

function managerSubtitle(count: number, uploading: { name: string; progress: number } | null): string {
  if (uploading) return `Upload ${uploading.progress}%`;
  return `${count} fichier${count > 1 ? "s" : ""}`;
}

function canPreviewImage(attachment: AttachmentRecord): boolean {
  return attachment.kind === "image" && ["image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"].includes(attachment.mimeType);
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} o`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} Ko`;
  return `${(value / (1024 * 1024)).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} Mo`;
}
